import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Heart, BadgeCheck, MapPin, Minus, Plus, Truck, ShoppingCart, Zap, Store as StoreIcon } from 'lucide-react';
import { fmtRM } from '../data/mockData';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { PriceDisplay } from '../components/commerce/PriceDisplay';
import { RatingStars } from '../components/commerce/RatingStars';
import { ProductCard } from '../components/commerce/ProductCard';
import { toast } from 'sonner';
import { fileUrl } from '../lib/upload';
import { useOpenStatus } from '../hooks/useOpenStatus';
import { waLink } from '../lib/wa';
import { CalendarClock } from 'lucide-react';

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, favourites, toggleFavourite, follows, toggleFollow, markViewed, deliverySettings, customProducts, merchantById } = useApp();
  const product = customProducts.find((p) => p.slug === slug);
  const { user } = useAuth();
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    if (product) markViewed(product.id);
  }, [product, markViewed]);

  const merchant = product ? merchantById(product.merchantId) : null;
  const openStatus = useOpenStatus(merchant?.id);
  const mtype = (merchant?.merchantType || 'PRODUCT').toUpperCase();
  const isService = mtype === 'SERVICE';
  const fnbClosed = mtype === 'FNB' && openStatus && !openStatus.open;
  if (!product || !merchant) {
    return <div className="mx-auto max-w-[1280px] px-4 py-20 text-center text-kelate-soft">Product not found.</div>;
  }
  const related = customProducts.filter((p) => p.id !== product.id && (p.merchantId === product.merchantId || p.category === product.category)).slice(0, 4);
  const isFav = favourites.includes(product.id);
  const isFollowing = follows.includes(merchant.id);

  const requireAuth = (fn) => () => {
    if (!user) {
      toast.info('Log in to continue');
      navigate(`/account/login?redirect=/product/${slug}`);
      return;
    }
    fn();
  };

  const buyNow = () => {
    addToCart(product, qty, variant);
    navigate('/checkout', { state: { buyNow: true } });
  };

  return (
    <div data-testid="product-page" className="mx-auto max-w-[1280px] px-4 py-8 pb-24 md:pb-8">
      <nav className="mb-4 text-xs text-kelate-soft">
        <Link to="/" className="hover:text-kelate-wood">Home</Link> / <Link to="/marketplace" className="hover:text-kelate-wood">Marketplace</Link> / <span className="text-kelate-ink">{product.name}</span>
      </nav>
      <div className="grid gap-8 lg:grid-cols-2">
        <div className="overflow-hidden rounded-2xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
          {product.image ? (
            <img src={fileUrl(product.image)} alt={product.name} className="aspect-square w-full object-cover" />
          ) : (
            <div className="flex aspect-square w-full items-center justify-center bg-secondary/50 font-display text-6xl font-semibold text-kelate-soft">{product.name[0]}</div>
          )}
        </div>
        <div>
          <div className="flex items-start justify-between gap-4">
            <h1 className="font-display text-2xl font-semibold text-kelate-darkwood sm:text-3xl" data-testid="product-name">{product.name}</h1>
            <button data-testid="product-fav-btn" onClick={requireAuth(() => toggleFavourite(product.id))} className="rounded-full border border-kelate-line bg-kelate-surface p-2 hover:border-kelate-red">
              <Heart size={16} className={isFav ? 'fill-kelate-red text-kelate-red' : 'text-kelate-soft'} />
            </button>
          </div>
          <Link to={`/store/${merchant.slug}`} className="mt-1 flex items-center gap-1.5 text-sm text-kelate-soft hover:text-kelate-wood">
            {merchant.name} {merchant.verified && <BadgeCheck size={14} className="text-kelate-sage" />}
          </Link>
          <div className="mt-2 flex items-center gap-3">
            <RatingStars rating={product.rating} count={product.reviews?.length || 0} />
            <span className="text-xs text-kelate-soft">{product.sold} sold</span>
            <span className="flex items-center gap-1 text-xs text-kelate-soft"><MapPin size={11} /> {product.district}</span>
          </div>
          <div className="mt-4 rounded-2xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
            <PriceDisplay price={product.price} original={product.originalPrice} size="lg" />
            {product.originalPrice && <span className="text-xs font-medium text-kelate-red">Save {fmtRM(product.originalPrice - product.price)}</span>}
          </div>

          {product.variants?.length > 0 && (
            <div className="mt-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-kelate-soft">Variant</div>
              <div className="flex flex-wrap gap-2" data-testid="variant-selector">
                {product.variants.map((v) => (
                  <button
                    key={v}
                    data-testid={`variant-${v.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setVariant(v)}
                    className={`rounded-sm border px-3 py-1.5 text-sm ${variant === v ? 'border-kelate-darkwood bg-kelate-darkwood text-kelate-surface' : 'border-kelate-line bg-kelate-surface text-kelate-ink hover:border-kelate-wood'}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center gap-4">
            <div className="flex items-center rounded-sm border border-kelate-line bg-kelate-surface" data-testid="quantity-selector">
              <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-2 text-kelate-soft hover:text-kelate-ink"><Minus size={14} /></button>
              <span className="w-10 text-center text-sm font-medium">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(Math.min(product.stock, qty + 1))} className="p-2 text-kelate-soft hover:text-kelate-ink"><Plus size={14} /></button>
            </div>
            <span className="text-xs text-kelate-soft">{product.stock} in stock</span>
          </div>

          <div className="mt-5 hidden gap-3 md:flex">
            {isService ? (
              <a data-testid="make-appointment-btn" href={waLink(merchant?.whatsapp || merchant?.phone, `Hi ${merchant?.name}, I would like to make an appointment for ${product.name}.`)} target="_blank" rel="noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-sm bg-kelate-sage px-4 py-3 text-sm font-medium text-white hover:opacity-90">
                <CalendarClock size={16} /> Make Appointment
              </a>
            ) : fnbClosed ? (
              <div data-testid="fnb-closed-note" className="flex flex-1 items-center justify-center rounded-sm bg-kelate-red/10 px-4 py-3 text-sm text-kelate-red">
                Shop closed — order during operating hours
              </div>
            ) : (
              <>
                <button
                  data-testid="add-to-cart-btn"
                  onClick={requireAuth(() => addToCart(product, qty, variant))}
                  className="flex flex-1 items-center justify-center gap-2 rounded-sm border border-kelate-darkwood px-4 py-3 text-sm font-medium text-kelate-darkwood hover:bg-secondary"
                >
                  <ShoppingCart size={16} /> Add to Cart
                </button>
                <button
                  data-testid="buy-now-btn"
                  onClick={requireAuth(buyNow)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-sm bg-kelate-darkwood px-4 py-3 text-sm font-medium text-kelate-surface hover:bg-kelate-wood"
                >
                  <Zap size={16} /> Buy Now
                </button>
              </>
            )}
          </div>

          <div className="mt-5 rounded-2xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4 text-sm">
            <div className="flex items-center gap-2 font-medium text-kelate-ink"><Truck size={15} className="text-kelate-sage" /> KELATE Delivery</div>
            <ul className="mt-2 space-y-1 text-xs text-kelate-soft">
              <li>Kota Bharu (within {deliverySettings.radiusKm} KM) — {fmtRM(deliverySettings.localFee)}</li>
              <li>Rest of Kelantan — {fmtRM(deliverySettings.kelantanFee)}</li>
              <li>Outside Kelantan — {fmtRM(deliverySettings.outsideFee)}</li>
            </ul>
            <p className="mt-2 text-xs font-medium text-kelate-darkwood">Pay on delivery: Cash, TNG QR or Bank Transfer.</p>
          </div>

          <div className="mt-4 flex items-center justify-between rounded-2xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
            <div className="flex items-center gap-3">
              <img src={fileUrl(merchant.logo)} alt="" className="h-11 w-11 rounded-md border border-kelate-line object-cover" />
              <div>
                <div className="flex items-center gap-1 text-sm font-semibold text-kelate-ink">{merchant.name} {merchant.verified && <BadgeCheck size={13} className="text-kelate-sage" />}</div>
                <div className="text-xs text-kelate-soft">{merchant.followers} followers · {merchant.rating} rating</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid="follow-store-btn" onClick={requireAuth(() => toggleFollow(merchant.id))} className={`rounded-sm border px-3 py-1.5 text-xs font-medium ${isFollowing ? 'border-kelate-sage text-[#5F7050]' : 'border-kelate-darkwood text-kelate-darkwood hover:bg-secondary'}`}>
                {isFollowing ? 'Following' : 'Follow'}
              </button>
              <Link to={`/store/${merchant.slug}`} data-testid="visit-store-btn" className="flex items-center gap-1 rounded-sm bg-kelate-darkwood px-3 py-1.5 text-xs font-medium text-kelate-surface hover:bg-kelate-wood">
                <StoreIcon size={12} /> Visit Store
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <section className="rounded-2xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5" data-testid="product-description">
          <h2 className="text-base font-semibold text-kelate-ink">Description</h2>
          <p className="mt-2 text-sm leading-relaxed text-kelate-soft">{product.description}</p>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-sm bg-secondary/60 px-3 py-2"><span className="text-kelate-soft">Category</span><div className="font-medium text-kelate-ink">{product.category}</div></div>
            <div className="rounded-sm bg-secondary/60 px-3 py-2"><span className="text-kelate-soft">Origin</span><div className="font-medium text-kelate-ink">{product.madeInKelantan ? 'Made in Kelantan' : 'Curated'}</div></div>
          </div>
        </section>
        <section className="rounded-2xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5" data-testid="product-reviews">
          <h2 className="text-base font-semibold text-kelate-ink">Reviews</h2>
          {product.reviews?.length ? (
            <div className="mt-3 space-y-3">
              {product.reviews.map((r, i) => (
                <div key={i} className="border-b border-kelate-line/60 pb-3 last:border-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-kelate-ink">{r.user}</span>
                    <RatingStars rating={r.rating} />
                  </div>
                  <p className="mt-1 text-sm text-kelate-soft">{r.text}</p>
                  <span className="text-[11px] text-kelate-soft">{r.date}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-sm text-kelate-soft">No reviews yet. Be the first after your order arrives.</p>
          )}
        </section>
      </div>

      {related.length > 0 && (
        <section className="mt-10" data-testid="related-products">
          <h2 className="mb-4 text-base md:text-lg font-semibold text-kelate-ink">Related Products</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      <div className="fixed bottom-14 left-0 right-0 z-30 flex gap-2 border-t border-kelate-line bg-kelate-surface p-3 md:hidden" data-testid="mobile-sticky-cta">
        {isService ? (
          <a data-testid="make-appointment-btn-mobile" href={waLink(merchant?.whatsapp || merchant?.phone, `Hi ${merchant?.name}, I would like to make an appointment for ${product.name}.`)} target="_blank" rel="noreferrer" className="flex flex-1 items-center justify-center gap-1.5 rounded-sm bg-kelate-sage py-2.5 text-sm font-medium text-white">
            <CalendarClock size={15} /> Make Appointment
          </a>
        ) : fnbClosed ? (
          <div data-testid="fnb-closed-note-mobile" className="flex flex-1 items-center justify-center rounded-sm bg-kelate-red/10 py-2.5 text-sm text-kelate-red">
            Shop closed — order during operating hours
          </div>
        ) : (
          <>
            <button data-testid="add-to-cart-btn-mobile" onClick={requireAuth(() => addToCart(product, qty, variant))} className="flex flex-1 items-center justify-center gap-1.5 rounded-sm border border-kelate-darkwood py-2.5 text-sm font-medium text-kelate-darkwood">
              <ShoppingCart size={15} /> Add to Cart
            </button>
            <button data-testid="buy-now-btn-mobile" onClick={requireAuth(buyNow)} className="flex-1 rounded-sm bg-kelate-darkwood py-2.5 text-sm font-medium text-kelate-surface">
              Buy Now
            </button>
          </>
        )}
      </div>
    </div>
  );
}
